/**
 * Feedback — header feedback dialog (bug reports / feature requests / general)
 *
 * An icon trigger in the site header opens a small modal (FlightForge-style):
 * type toggle + message box. Agent NPN/name, page, and browser info are
 * auto-attached so agents never retype context. Submits to /feedback/submit
 * (public endpoint — skip-mode agents can report bugs too).
 */

import { AgentIdentity } from './agent-identity.js';

const API_BASE = '';
const MAX_TEXT = 4000;

// Mirrors netlify/functions/feedback-submit.js's caps — client-side checks are a
// fast-feedback convenience, the server is the real gate.
const MAX_ATTACHMENTS = 3;
const MAX_ATTACHMENT_TOTAL_BYTES = 4 * 1024 * 1024;
const ALLOWED_ATTACHMENT_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.pdf', '.txt', '.log', '.csv'];

export const FEEDBACK_TYPES = [
  { id: 'bug', label: '🐞 Bug' },
  { id: 'feature', label: '💡 Request' },
  { id: 'general', label: '💬 Feedback' },
];

const TYPE_IDS = FEEDBACK_TYPES.map((t) => t.id);

const PLACEHOLDERS = {
  bug: 'What broke, and what did you expect to happen?',
  feature: 'What should we build or improve?',
  general: 'Tell us anything — what’s working, what’s not.',
};

/**
 * Validate a feedback draft. Mirrors the server-side rules.
 * @returns {string|null} error message, or null when valid
 */
export function validateFeedback({ type, text } = {}) {
  if (!TYPE_IDS.includes(type)) return 'Pick a feedback type';
  const trimmed = typeof text === 'string' ? text.trim() : '';
  if (!trimmed) return 'Please describe your feedback';
  if (trimmed.length > MAX_TEXT) return `Keep it under ${MAX_TEXT} characters`;
  return null;
}

/**
 * Build the submit payload: trimmed text + only the context fields that have values.
 */
export function buildFeedbackPayload({ type, text, agentNpn, agentName, page, userAgent, attachments } = {}) {
  const payload = { type, text: (text || '').trim() };
  const context = { agentNpn, agentName, page, userAgent };
  for (const [field, value] of Object.entries(context)) {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    if (trimmed) payload[field] = trimmed;
  }
  if (Array.isArray(attachments) && attachments.length) {
    payload.attachments = attachments.map(({ filename, contentType, dataBase64 }) => ({ filename, contentType, dataBase64 }));
  }
  return payload;
}

/**
 * Human-readable file size, e.g. "128 KB" / "1.4 MB".
 */
export function formatAttachmentSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function extensionOf(filename) {
  const idx = filename.lastIndexOf('.');
  return idx >= 0 ? filename.slice(idx).toLowerCase() : '';
}

function escapeHtml(str) {
  const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  return String(str).replace(/[&<>"']/g, (c) => map[c]);
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').replace(/^data:[^;]+;base64,/, ''));
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * POST feedback to the API.
 * @returns {Promise<Object>} { feedbackId }
 */
export async function submitFeedback(payload) {
  const response = await fetch(`${API_BASE}/feedback/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error || 'Failed to send feedback');
  }
  const result = await response.json();
  return result.data;
}

// ---------------------------------------------------------------------------
// Dialog DOM — injected lazily on first open so pages that never use it pay
// nothing. Reuses the shared .wq-modal styles (see styles.css §14).
// ---------------------------------------------------------------------------

let selectedType = 'bug';
let attachments = []; // { filename, contentType, dataBase64, size }
let pasteCounter = 0;

function modalEl() {
  return document.getElementById('wq-feedback-modal');
}

function buildModal() {
  const wrap = document.createElement('div');
  wrap.innerHTML = `
    <div id="wq-feedback-modal" class="wq-modal" role="dialog" aria-modal="true" aria-labelledby="wq-feedback-title">
      <div class="wq-modal__backdrop" data-feedback-close></div>
      <div class="wq-modal__content">
        <button type="button" class="wq-modal__close" aria-label="Close" data-feedback-close>&times;</button>
        <h2 id="wq-feedback-title">Feedback</h2>
        <p class="wq-modal__subtitle">Spotted a bug or want something added? Send it over.</p>
        <div id="wq-feedback-form-view">
          <div class="wq-feedback-types" role="group" aria-label="Feedback type">
            ${FEEDBACK_TYPES.map((t) => `
              <button type="button" class="wq-feedback-type" data-feedback-type="${t.id}" aria-pressed="${t.id === selectedType}">${t.label}</button>
            `).join('')}
          </div>
          <textarea id="wq-feedback-text" rows="4" maxlength="${MAX_TEXT}" aria-label="Your feedback" placeholder="${PLACEHOLDERS[selectedType]}"></textarea>
          <div class="wq-feedback-attach">
            <label for="wq-feedback-file-input" class="wq-feedback-attach-btn">📎 Attach screenshot or file</label>
            <input type="file" id="wq-feedback-file-input" multiple accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.log,.csv" hidden />
          </div>
          <ul id="wq-feedback-attachments" class="wq-feedback-attachments" hidden></ul>
          <p id="wq-feedback-error" class="wq-feedback-error" role="alert" hidden></p>
          <div class="wq-feedback-actions">
            <button type="button" class="wq-feedback-cancel" data-feedback-close>Cancel</button>
            <button type="button" id="wq-feedback-send" class="wq-feedback-send">Send feedback</button>
          </div>
        </div>
        <div id="wq-feedback-done-view" hidden>
          <p class="wq-feedback-done-title">Got it — thank you!</p>
          <p class="wq-modal__subtitle">Your feedback goes straight to the Woxom team.</p>
          <div class="wq-feedback-actions">
            <button type="button" class="wq-feedback-send" data-feedback-close>Done</button>
          </div>
        </div>
      </div>
    </div>`;
  const modal = wrap.firstElementChild;
  document.body.appendChild(modal);

  modal.addEventListener('click', (e) => {
    if (e.target.closest('[data-feedback-close]')) close();
    const typeBtn = e.target.closest('[data-feedback-type]');
    if (typeBtn) setType(typeBtn.getAttribute('data-feedback-type'));
    const removeBtn = e.target.closest('[data-attachment-remove]');
    if (removeBtn) removeAttachment(Number(removeBtn.getAttribute('data-attachment-remove')));
  });
  modal.querySelector('#wq-feedback-send').addEventListener('click', send);
  modal.querySelector('#wq-feedback-file-input').addEventListener('change', (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-selecting the same file after removal
    addAttachmentFiles(files);
  });
  modal.addEventListener('paste', handlePaste);
  return modal;
}

function setType(type) {
  selectedType = type;
  const modal = modalEl();
  if (!modal) return;
  modal.querySelectorAll('[data-feedback-type]').forEach((btn) => {
    btn.setAttribute('aria-pressed', String(btn.getAttribute('data-feedback-type') === type));
  });
  modal.querySelector('#wq-feedback-text').placeholder = PLACEHOLDERS[type];
}

function showError(message) {
  const errorEl = modalEl()?.querySelector('#wq-feedback-error');
  if (!errorEl) return;
  errorEl.textContent = message || '';
  errorEl.hidden = !message;
}

function escHandler(e) {
  if (e.key === 'Escape') close();
}

function attachmentsTotalBytes() {
  return attachments.reduce((sum, a) => sum + a.size, 0);
}

function renderAttachments() {
  const list = modalEl()?.querySelector('#wq-feedback-attachments');
  if (!list) return;
  list.hidden = attachments.length === 0;
  list.innerHTML = attachments.map((a, i) => `
    <li class="wq-feedback-attachment">
      <span class="wq-feedback-attachment__name">${escapeHtml(a.filename)}</span>
      <span class="wq-feedback-attachment__size">${formatAttachmentSize(a.size)}</span>
      <button type="button" class="wq-feedback-attachment__remove" data-attachment-remove="${i}" aria-label="Remove ${escapeHtml(a.filename)}">&times;</button>
    </li>
  `).join('');
}

function resetAttachments() {
  attachments = [];
  pasteCounter = 0;
  renderAttachments();
  const fileInput = modalEl()?.querySelector('#wq-feedback-file-input');
  if (fileInput) {
    fileInput.value = '';
    fileInput.disabled = false;
  }
}

function removeAttachment(index) {
  attachments.splice(index, 1);
  renderAttachments();
}

/**
 * Read + validate dropped/selected/pasted files client-side (fast feedback —
 * the server in feedback-submit.js is the real gate) and append to `attachments`.
 */
async function addAttachmentFiles(files) {
  showError(null);
  for (const file of files) {
    if (attachments.length >= MAX_ATTACHMENTS) {
      showError(`You can attach up to ${MAX_ATTACHMENTS} files`);
      break;
    }
    const ext = extensionOf(file.name || '');
    if (!ALLOWED_ATTACHMENT_EXTENSIONS.includes(ext)) {
      showError(`Unsupported file type: ${file.name || '(unnamed)'}`);
      continue;
    }
    if (attachmentsTotalBytes() + file.size > MAX_ATTACHMENT_TOTAL_BYTES) {
      showError('Attachments exceed the 4 MB total limit');
      continue;
    }
    try {
      const dataBase64 = await readFileAsBase64(file);
      attachments.push({
        filename: file.name || 'file',
        contentType: file.type || 'application/octet-stream',
        dataBase64,
        size: file.size,
      });
    } catch (e) {
      showError('Could not read that file');
    }
  }
  renderAttachments();
}

/**
 * Clipboard image paste anywhere in the modal becomes an attachment
 * (named pasted-screenshot-<n>.png, matching what OS screenshot copies send).
 */
function handlePaste(e) {
  const items = e.clipboardData?.items;
  if (!items || !items.length) return;
  const imageFiles = [];
  for (const item of items) {
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const file = item.getAsFile();
      if (!file) continue;
      pasteCounter += 1;
      imageFiles.push(new File([file], `pasted-screenshot-${pasteCounter}.png`, { type: file.type || 'image/png' }));
    }
  }
  if (imageFiles.length) {
    e.preventDefault();
    addAttachmentFiles(imageFiles);
  }
}

export function open() {
  const modal = modalEl() || buildModal();
  // Fresh form each open
  modal.querySelector('#wq-feedback-text').value = '';
  modal.querySelector('#wq-feedback-form-view').hidden = false;
  modal.querySelector('#wq-feedback-done-view').hidden = true;
  const sendBtn = modal.querySelector('#wq-feedback-send');
  sendBtn.disabled = false;
  sendBtn.textContent = 'Send feedback';
  resetAttachments();
  showError(null);
  setType('bug');
  modal.classList.add('wq-modal--open');
  document.addEventListener('keydown', escHandler);
  modal.querySelector('#wq-feedback-text').focus();
}

export function close() {
  const modal = modalEl();
  if (!modal) return;
  modal.classList.remove('wq-modal--open');
  document.removeEventListener('keydown', escHandler);
  resetAttachments();
}

async function send() {
  const modal = modalEl();
  const text = modal.querySelector('#wq-feedback-text').value;

  const draft = { type: selectedType, text };
  const validationError = validateFeedback(draft);
  if (validationError) {
    showError(validationError);
    return;
  }

  const sendBtn = modal.querySelector('#wq-feedback-send');
  const fileInput = modal.querySelector('#wq-feedback-file-input');
  sendBtn.disabled = true;
  sendBtn.textContent = 'Sending…';
  if (fileInput) fileInput.disabled = true;
  showError(null);

  try {
    const payload = buildFeedbackPayload({
      ...draft,
      agentNpn: AgentIdentity.getAgentNpn() || '',
      agentName: AgentIdentity.getCachedProfile()?.name || '',
      page: window.location.pathname + window.location.search,
      userAgent: navigator.userAgent,
      attachments,
    });
    await submitFeedback(payload);
    resetAttachments();
    modal.querySelector('#wq-feedback-form-view').hidden = true;
    modal.querySelector('#wq-feedback-done-view').hidden = false;
  } catch (err) {
    showError(err.message || 'Failed to send feedback');
    sendBtn.disabled = false;
    sendBtn.textContent = 'Send feedback';
    if (fileInput) fileInput.disabled = false;
  }
}

/**
 * Bind the header trigger button(s). Call once on page load.
 */
export function init() {
  document.querySelectorAll('[data-feedback-open]').forEach((btn) => {
    btn.addEventListener('click', open);
  });
}

export const Feedback = {
  FEEDBACK_TYPES,
  validateFeedback,
  buildFeedbackPayload,
  submitFeedback,
  open,
  close,
  init,
};
